import type {
  AnalyticsData,
} from '@/services/PagesServices/AnalyticsService';

// Brand colors
const COLORS = {
  primary: [44, 62, 80] as [number, number, number],
  accent: [255, 140, 66] as [number, number, number],
  success: [22, 163, 74] as [number, number, number],
  danger: [220, 38, 38] as [number, number, number],
  warning: [245, 158, 11] as [number, number, number],
  text: [31, 41, 55] as [number, number, number],
  textLight: [107, 114, 128] as [number, number, number],
  border: [229, 231, 235] as [number, number, number],
  bgLight: [249, 250, 251] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  blue: [59, 130, 246] as [number, number, number],
  purple: [139, 92, 246] as [number, number, number],
  emerald: [16, 185, 129] as [number, number, number],
  indigo: [99, 102, 241] as [number, number, number],
};

/** Safely convert any value to a display string. null/undefined/'' become the fallback. */
const s = (val: any, fallback = '—'): string => {
  if (val == null || val === '') return fallback;
  return String(val);
};

/** Safely convert to number. null/undefined/NaN become 0. */
const n = (val: any): number => {
  const parsed = Number(val);
  return isNaN(parsed) ? 0 : parsed;
};

/** Truncate a string safely. */
const trunc = (val: any, max: number): string => {
  const str = s(val, '');
  return str.length > max ? str.substring(0, max) + '...' : str;
};

/** Parse hex color to RGB tuple */
const hexToRgb = (hex: string): [number, number, number] => {
  const clean = s(hex, '#2c3e50');
  const r = parseInt(clean.slice(1, 3), 16) || 44;
  const g = parseInt(clean.slice(3, 5), 16) || 62;
  const b = parseInt(clean.slice(5, 7), 16) || 80;
  return [r, g, b];
};

export const exportAnalyticsPDF = async (data: AnalyticsData) => {
  const jspdfModule = await import('jspdf') as any;
  const JsPDF = jspdfModule.jsPDF || jspdfModule.default;
  const doc = new JsPDF('p', 'mm', 'a4') as any;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // ===== HELPERS =====
  const checkPageBreak = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  const drawRoundedRect = (
    x: number, yPos: number, w: number, h: number, r: number,
    fillColor?: [number, number, number], strokeColor?: [number, number, number]
  ) => {
    if (fillColor) doc.setFillColor(...fillColor);
    if (strokeColor) {
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.3);
    }
    doc.roundedRect(x, yPos, w, h, r, r, fillColor && strokeColor ? 'FD' : fillColor ? 'F' : 'S');
  };

  const drawProgressBar = (
    x: number, yPos: number, w: number, h: number,
    percentage: number, fillColor: [number, number, number]
  ) => {
    drawRoundedRect(x, yPos, w, h, h / 2, [234, 237, 241]);
    const fillWidth = Math.max(h, (percentage / 100) * w);
    if (percentage > 0) drawRoundedRect(x, yPos, fillWidth, h, h / 2, fillColor);
  };

  const sectionTitle = (title: string, subtitle?: string) => {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS.primary);
    doc.text(title, margin, y);
    if (subtitle) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.textLight);
      doc.text(subtitle, margin, y + 6);
      y += 12;
    } else {
      y += 8;
    }
  };

  // ===== HEADER =====
  drawRoundedRect(margin, y, contentWidth, 28, 4, COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.white);
  doc.text('Analytics Report', margin + 10, y + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(200, 210, 220);
  doc.text('Magnetix Learning Platform', margin + 10, y + 19);

  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.white);
  doc.text(dateStr, pageWidth - margin - 10, y + 12, { align: 'right' });
  const timeStr = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  doc.text(`Generated at ${timeStr}`, pageWidth - margin - 10, y + 19, { align: 'right' });
  y += 35;

  // ===== KPI STATS =====
  if (data.dashboardStats && data.dashboardStats.length > 0) {
    sectionTitle('Overview Statistics');
    const cardWidth = (contentWidth - 9) / 4;
    const cardHeight = 30;

    data.dashboardStats.forEach((stat, i) => {
      const cardX = margin + i * (cardWidth + 3);
      drawRoundedRect(cardX, y, cardWidth, cardHeight, 3, COLORS.white, COLORS.border);
      doc.setFillColor(...COLORS.accent);
      doc.rect(cardX + 3, y, cardWidth - 6, 1.5, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.textLight);
      doc.text(s(stat.title), cardX + 5, y + 9);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...COLORS.text);
      doc.text(n(stat.value).toLocaleString(), cardX + 5, y + 20);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.success);
      doc.text(`+${s(stat.increase, '0')} from last week`, cardX + 5, y + 26);
    });
    y += cardHeight + 10;
  }

  // ===== QUICK INSIGHTS =====
  {
    const courseCompletion = data.courseCompletionData || [];
    const deptPerformance = data.combinedAnalytics?.departmentPerformance || [];
    const userReport = data.userReportData || [];
    const engagement = data.learningEngagement;

    const avgCompletionRate = courseCompletion.length > 0
      ? Math.round(courseCompletion.reduce((sum, c) => sum + n(c.completionRate), 0) / courseCompletion.length)
      : 0;
    const topDept = deptPerformance.length > 0
      ? [...deptPerformance].sort((a, b) => n(b.completionPercentage) - n(a.completionPercentage))[0]
      : null;
    const totalCerts = userReport.reduce((sum, u) => sum + n(u.certificatesEarned), 0);
    const enrollmentRate = engagement
      ? Math.round((n(engagement.usersWithEnrollments) / Math.max(n(engagement.totalActiveUsers), 1)) * 100)
      : 0;

    if (avgCompletionRate > 0 || topDept || totalCerts > 0 || enrollmentRate > 0) {
      checkPageBreak(25);
      sectionTitle('Quick Insights');

      const insightItems = [
        { label: 'Avg Completion Rate', value: `${avgCompletionRate}%` },
        { label: 'Top Department', value: topDept ? s(topDept.department) : '—' },
        { label: 'Total Certifications', value: String(totalCerts) },
        { label: 'Enrollment Rate', value: `${enrollmentRate}%` },
      ];

      const chipW = (contentWidth - 9) / 4;
      drawRoundedRect(margin, y, contentWidth, 20, 3, COLORS.white, COLORS.border);

      insightItems.forEach((item, i) => {
        const ix = margin + 4 + i * (chipW + 3);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...COLORS.primary);
        doc.text(item.value, ix + chipW / 2, y + 8, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...COLORS.textLight);
        doc.text(item.label, ix + chipW / 2, y + 14, { align: 'center' });
      });
      y += 28;
    }
  }

  // ===== PLATFORM HEALTH SCORE =====
  {
    const courseCompletion = data.courseCompletionData || [];
    const deptPerformance = data.combinedAnalytics?.departmentPerformance || [];
    const engagement = data.learningEngagement;

    const avgCompletionRate = courseCompletion.length > 0
      ? Math.round(courseCompletion.reduce((sum, c) => sum + n(c.completionRate), 0) / courseCompletion.length)
      : 0;
    const enrollmentRate = engagement
      ? Math.round((n(engagement.usersWithEnrollments) / Math.max(n(engagement.totalActiveUsers), 1)) * 100)
      : 0;

    let score = 0;
    let factors = 0;
    if (avgCompletionRate > 0) { score += Math.min(avgCompletionRate, 100); factors++; }
    if (enrollmentRate > 0) { score += Math.min(enrollmentRate, 100); factors++; }
    if (engagement && n(engagement.totalActiveUsers) > 0) {
      const activeRatio = Math.min((n(engagement.activeUsersLast7Days) / n(engagement.totalActiveUsers)) * 100, 100);
      score += activeRatio; factors++;
    }
    if (deptPerformance.length > 0) {
      const avgDeptCompletion = deptPerformance.reduce((s, d) => s + n(d.completionPercentage), 0) / deptPerformance.length;
      score += Math.min(avgDeptCompletion, 100); factors++;
    }
    const healthScore = factors > 0 ? Math.round(score / factors) : 0;

    if (healthScore > 0) {
      checkPageBreak(45);
      sectionTitle('Platform Health Score', 'Composite score based on key performance metrics');

      drawRoundedRect(margin, y, contentWidth, 35, 3, COLORS.white, COLORS.border);

      // Draw gauge as a semicircle arc
      const gaugeX = margin + 35;
      const gaugeY = y + 25;
      const gaugeR = 18;

      // Background arc
      doc.setDrawColor(234, 237, 241);
      doc.setLineWidth(3);
      for (let angle = 180; angle <= 360; angle += 2) {
        const rad = (angle * Math.PI) / 180;
        const x1 = gaugeX + gaugeR * Math.cos(rad);
        const y1 = gaugeY + gaugeR * Math.sin(rad);
        const rad2 = ((angle + 2) * Math.PI) / 180;
        const x2 = gaugeX + gaugeR * Math.cos(rad2);
        const y2 = gaugeY + gaugeR * Math.sin(rad2);
        doc.line(x1, y1, x2, y2);
      }

      // Filled arc based on score
      const scoreAngle = 180 + (healthScore / 100) * 180;
      const scoreColor: [number, number, number] = healthScore >= 80 ? COLORS.success : healthScore >= 60 ? COLORS.blue : healthScore >= 40 ? COLORS.warning : COLORS.danger;
      doc.setDrawColor(...scoreColor);
      doc.setLineWidth(3);
      for (let angle = 180; angle < scoreAngle; angle += 2) {
        const rad = (angle * Math.PI) / 180;
        const x1 = gaugeX + gaugeR * Math.cos(rad);
        const y1 = gaugeY + gaugeR * Math.sin(rad);
        const rad2 = ((angle + 2) * Math.PI) / 180;
        const x2 = gaugeX + gaugeR * Math.cos(rad2);
        const y2 = gaugeY + gaugeR * Math.sin(rad2);
        doc.line(x1, y1, x2, y2);
      }

      // Score text in center
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(...scoreColor);
      doc.text(`${healthScore}`, gaugeX, gaugeY - 2, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text('/100', gaugeX + 10, gaugeY - 2);

      // Status label
      const statusLabel = healthScore >= 80 ? 'Excellent' : healthScore >= 60 ? 'Good' : healthScore >= 40 ? 'Fair' : 'Needs Attention';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...scoreColor);
      doc.text(statusLabel, margin + 80, y + 14);

      // Breakdown
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      const breakdownItems = [
        `Completion Rate: ${avgCompletionRate}%`,
        `Enrollment Rate: ${enrollmentRate}%`,
        engagement ? `7-Day Active: ${Math.round((n(engagement.activeUsersLast7Days) / Math.max(n(engagement.totalActiveUsers), 1)) * 100)}%` : '',
        deptPerformance.length > 0 ? `Avg Dept Performance: ${Math.round(deptPerformance.reduce((s, d) => s + n(d.completionPercentage), 0) / deptPerformance.length)}%` : '',
      ].filter(Boolean);
      breakdownItems.forEach((item, i) => {
        doc.text(item, margin + 80, y + 21 + i * 4);
      });

      y += 42;
    }
  }

  // ===== ENROLLMENT FUNNEL =====
  if (data.learningEngagement && data.progressDistribution) {
    checkPageBreak(55);
    sectionTitle('Enrollment Funnel', 'Conversion through learning stages');

    const eng = data.learningEngagement;
    const pd = data.progressDistribution;
    const totalEnrolled = n(eng.totalEnrollments);
    const started = totalEnrolled - n(pd.notStarted);
    const inProgress = n(pd.intermediate) + n(pd.advanced) + n(pd.completed);
    const completed = n(pd.completed);

    const funnelStages = [
      { label: 'Enrolled', value: totalEnrolled, color: COLORS.blue },
      { label: 'Started', value: started, color: COLORS.purple },
      { label: 'In Progress', value: inProgress, color: COLORS.warning },
      { label: 'Completed', value: completed, color: COLORS.emerald },
    ];

    const funnelH = 40;
    const maxW = contentWidth - 20;
    const maxVal = Math.max(totalEnrolled, 1);

    drawRoundedRect(margin, y, contentWidth, funnelH + 8, 3, COLORS.white, COLORS.border);

    funnelStages.forEach((stage, i) => {
      const barW = Math.max(20, (stage.value / maxVal) * maxW);
      const barX = margin + 10 + (maxW - barW) / 2;
      const barY = y + 4 + i * 10;

      doc.setFillColor(...stage.color);
      doc.roundedRect(barX, barY, barW, 7, 2, 2, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.white);
      doc.text(`${stage.label}: ${stage.value}`, barX + barW / 2, barY + 5, { align: 'center' });

      // Conversion rate
      if (i > 0) {
        const prevVal = funnelStages[i - 1].value;
        const rate = prevVal > 0 ? Math.round((stage.value / prevVal) * 100) : 0;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(...COLORS.textLight);
        doc.text(`${rate}%`, margin + 4, barY + 5);
      }
    });

    y += funnelH + 16;
  }

  // ===== PROGRESS DISTRIBUTION =====
  if (data.progressDistribution) {
    const pd = data.progressDistribution;
    const ns = n(pd.notStarted);
    const bg = n(pd.beginner);
    const im = n(pd.intermediate);
    const ad = n(pd.advanced);
    const cm = n(pd.completed);
    const total = ns + bg + im + ad + cm;

    if (total > 0) {
      checkPageBreak(40);
      sectionTitle('Progress Distribution', 'Learner progress across all courses');

      const segments = [
        { label: 'Not Started', value: ns, pct: Math.round((ns / total) * 100), color: [229, 231, 235] as [number, number, number] },
        { label: '1-25%', value: bg, pct: Math.round((bg / total) * 100), color: [251, 191, 36] as [number, number, number] },
        { label: '26-50%', value: im, pct: Math.round((im / total) * 100), color: [249, 115, 22] as [number, number, number] },
        { label: '51-99%', value: ad, pct: Math.round((ad / total) * 100), color: COLORS.blue },
        { label: 'Completed', value: cm, pct: Math.round((cm / total) * 100), color: COLORS.emerald },
      ];

      // Stacked bar
      drawRoundedRect(margin, y, contentWidth, 26, 3, COLORS.white, COLORS.border);
      const barY = y + 4;
      const barH = 8;
      let barX = margin + 4;
      const barTotalW = contentWidth - 8;

      segments.forEach((seg) => {
        const segW = (seg.pct / 100) * barTotalW;
        if (segW > 0) {
          doc.setFillColor(...seg.color);
          doc.rect(barX, barY, segW, barH, 'F');
          barX += segW;
        }
      });

      // Legend below bar
      let legendX = margin + 4;
      const legendY = barY + barH + 5;
      segments.forEach((seg) => {
        doc.setFillColor(...seg.color);
        doc.circle(legendX + 2, legendY, 1.5, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(...COLORS.text);
        const legendText = `${seg.label} ${seg.pct}%`;
        doc.text(legendText, legendX + 5, legendY + 1);
        legendX += doc.getTextWidth(legendText) + 10;
      });

      y += 34;
    }
  }

  // ===== LEARNING ENGAGEMENT =====
  if (data.learningEngagement) {
    checkPageBreak(40);
    sectionTitle('Learning Engagement', 'Platform engagement metrics');
    const eng = data.learningEngagement;
    const items = [
      { label: 'Total Active Users', value: n(eng.totalActiveUsers) },
      { label: 'Users with Enrollments', value: n(eng.usersWithEnrollments) },
      { label: 'Total Enrollments', value: n(eng.totalEnrollments) },
      { label: 'Total Time Spent (hrs)', value: n(eng.totalTimeSpent) },
      { label: 'Avg Time/User (hrs)', value: n(eng.avgTimePerUser) },
      { label: 'Active Last 7 Days', value: n(eng.activeUsersLast7Days) },
    ];

    const colW = (contentWidth - 6) / 3;
    const rowH = 20;
    drawRoundedRect(margin, y, contentWidth, rowH * 2 + 4, 3, COLORS.white, COLORS.border);

    items.forEach((item, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const ix = margin + 4 + col * (colW + 2);
      const iy = y + 4 + row * rowH;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...COLORS.primary);
      doc.text(String(item.value), ix + colW / 2, iy + 8, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(item.label, ix + colW / 2, iy + 14, { align: 'center' });
    });
    y += rowH * 2 + 12;
  }

  // ===== LEARNING HOURS TREND =====
  if (data.learningHours && data.learningHours.length > 0) {
    checkPageBreak(85);
    sectionTitle('Learning Hours Trend', 'Weekly learning hours across the platform');

    const chartX = margin + 12;
    const chartY = y;
    const chartW = contentWidth - 20;
    const chartH = 55;

    drawRoundedRect(margin, chartY - 4, contentWidth, chartH + 18, 3, COLORS.white, COLORS.border);

    const hours = data.learningHours.map(d => n(d.hours));
    const maxH = Math.max(...hours, 1);
    const ySteps = 4;

    doc.setFontSize(7);
    doc.setTextColor(...COLORS.textLight);
    for (let i = 0; i <= ySteps; i++) {
      const val = Math.round((maxH / ySteps) * (ySteps - i));
      const lineY = chartY + (i / ySteps) * chartH;
      doc.text(String(val), margin + 3, lineY + 1);
      doc.setDrawColor(240, 240, 240);
      doc.setLineWidth(0.2);
      doc.line(chartX, lineY, chartX + chartW, lineY);
    }

    const points: { x: number; y: number }[] = [];
    const totalPoints = data.learningHours.length;
    const divisor = totalPoints > 1 ? totalPoints - 1 : 1;
    data.learningHours.forEach((d, i) => {
      points.push({
        x: chartX + (i / divisor) * chartW,
        y: chartY + chartH - (n(d.hours) / maxH) * chartH,
      });
    });

    doc.setDrawColor(...COLORS.accent);
    doc.setLineWidth(0.8);
    for (let i = 1; i < points.length; i++) {
      doc.line(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
    }

    points.forEach((p, i) => {
      doc.setFillColor(...COLORS.accent);
      doc.circle(p.x, p.y, 1.5, 'F');
      doc.setFillColor(...COLORS.white);
      doc.circle(p.x, p.y, 0.8, 'F');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.text);
      doc.text(s(data.learningHours![i].hours, '0'), p.x, p.y - 3, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(s(data.learningHours![i].week, ''), p.x, chartY + chartH + 6, { align: 'center' });
    });

    y = chartY + chartH + 18;
  }

  // ===== LEARNING PROGRESS =====
  if (data.learningProgress) {
    checkPageBreak(35);
    const lp = data.learningProgress;
    drawRoundedRect(margin, y, contentWidth, 28, 3, COLORS.white, COLORS.border);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.primary);
    doc.text(`Learning Progress — ${s(lp.currentMonth, '')}`, margin + 8, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.text);
    doc.text(`${n(lp.totalHours)} Hours Total`, margin + 8, y + 18);

    const trendColor = lp.trend === 'down' ? COLORS.danger : COLORS.success;
    const trendArrow = lp.trend === 'down' ? '\u2193' : '\u2191';
    doc.setTextColor(...trendColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`${trendArrow} ${n(lp.progressPercentage)}%`, margin + 50, y + 18);

    const pbX = contentWidth / 2 + margin;
    const pbW = contentWidth / 2 - 16;
    drawProgressBar(pbX, y + 12, pbW, 5, Math.min(n(lp.progressPercentage), 100), COLORS.accent);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`${Math.min(n(lp.progressPercentage), 100)}% of monthly target`, pbX, y + 23);
    y += 35;
  }

  // ===== LEADERBOARD =====
  if (data.combinedAnalytics?.leaderboard && data.combinedAnalytics.leaderboard.length > 0) {
    checkPageBreak(60);
    sectionTitle('Top Learners', 'Leaderboard rankings');

    const tableX = margin;
    drawRoundedRect(tableX, y, contentWidth, 9, 2, COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.white);
    doc.text('#', tableX + 4, y + 6);
    doc.text('Name', tableX + 14, y + 6);
    doc.text('Level', tableX + 70, y + 6);
    doc.text('Points', tableX + 100, y + 6);
    doc.text('Courses', tableX + 125, y + 6);
    doc.text('Certs', tableX + 150, y + 6);
    y += 10;

    data.combinedAnalytics.leaderboard.slice(0, 10).forEach((entry, i) => {
      checkPageBreak(12);
      const rowY = y;
      const bgColor = i % 2 === 0 ? COLORS.bgLight : COLORS.white;
      drawRoundedRect(tableX, rowY, contentWidth, 10, 1, bgColor);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.text);
      doc.text(s(entry.rank, ''), tableX + 5, rowY + 7);

      doc.setFont('helvetica', 'normal');
      doc.text(trunc(entry.name, 25), tableX + 14, rowY + 7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(s(entry.level, '—'), tableX + 70, rowY + 7);
      doc.setTextColor(...COLORS.primary);
      doc.setFont('helvetica', 'bold');
      doc.text(s(entry.points, '0'), tableX + 100, rowY + 7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.text);
      doc.text(s(entry.coursesCompleted, '0'), tableX + 130, rowY + 7);
      doc.text(s(entry.certificatesEarned, '0'), tableX + 155, rowY + 7);
      y += 11;
    });
    y += 8;
  }

  // ===== DEPARTMENT PERFORMANCE =====
  if (data.combinedAnalytics?.departmentPerformance && data.combinedAnalytics.departmentPerformance.length > 0) {
    checkPageBreak(50);
    sectionTitle('Department Performance', 'Completion rates by department');

    data.combinedAnalytics.departmentPerformance.forEach((dept) => {
      checkPageBreak(16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      doc.text(s(dept.department), margin + 4, y + 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(`${n(dept.usersWithEnrollments)}/${n(dept.totalUsers)} users`, margin + 80, y + 4);

      const pct = n(dept.completionPercentage);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(pct >= 70 ? COLORS.success : pct >= 40 ? COLORS.warning : COLORS.danger));
      doc.text(`${pct}%`, pageWidth - margin - 4, y + 4, { align: 'right' });

      drawProgressBar(margin + 4, y + 7, contentWidth - 8, 4, pct,
        pct >= 70 ? COLORS.success : pct >= 40 ? COLORS.warning : COLORS.danger);
      y += 16;
    });
    y += 8;
  }

  // ===== TOP COURSES =====
  if (data.topCourses && data.topCourses.length > 0) {
    checkPageBreak(60);
    sectionTitle('Top Courses', 'Most enrolled courses');

    const tableX = margin;
    drawRoundedRect(tableX, y, contentWidth, 9, 2, COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.white);
    doc.text('#', tableX + 4, y + 6);
    doc.text('Course Name', tableX + 14, y + 6);
    doc.text('Enrolled', tableX + contentWidth - 55, y + 6);
    doc.text('Progress', tableX + contentWidth - 30, y + 6);
    y += 10;

    data.topCourses.forEach((course, i) => {
      checkPageBreak(14);
      const rowY = y;
      const bgColor = i % 2 === 0 ? COLORS.bgLight : COLORS.white;
      drawRoundedRect(tableX, rowY, contentWidth, 12, 1, bgColor);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.textLight);
      doc.text(String(i + 1), tableX + 5, rowY + 8);

      doc.setTextColor(...COLORS.text);
      doc.setFont('helvetica', 'bold');
      doc.text(trunc(course.name, 40), tableX + 14, rowY + 8);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.textLight);
      doc.text(`${n(course.enrolled)} / ${n(course.total)}`, tableX + contentWidth - 55, rowY + 8);

      const pct = n(course.total) > 0 ? (n(course.enrolled) / n(course.total)) * 100 : 0;
      drawProgressBar(tableX + contentWidth - 32, rowY + 4, 28, 3.5, pct, COLORS.accent);
      y += 13;
    });
    y += 8;
  }

  // ===== COURSES BY CATEGORY =====
  if (data.categoryBreakdown && data.categoryBreakdown.length > 0) {
    checkPageBreak(40);
    sectionTitle('Courses by Category', 'Category-wise course and enrollment breakdown');

    const tableX = margin;
    drawRoundedRect(tableX, y, contentWidth, 9, 2, COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.white);
    doc.text('Category', tableX + 10, y + 6);
    doc.text('Courses', tableX + 75, y + 6);
    doc.text('Enrollments', tableX + 105, y + 6);
    doc.text('Completion Rate', tableX + 140, y + 6);
    y += 10;

    data.categoryBreakdown.forEach((cat, i) => {
      checkPageBreak(11);
      const rowY = y;
      const bgColor = i % 2 === 0 ? COLORS.bgLight : COLORS.white;
      drawRoundedRect(tableX, rowY, contentWidth, 10, 1, bgColor);

      // Color dot
      const rgb = hexToRgb(cat.color);
      doc.setFillColor(...rgb);
      doc.circle(tableX + 5, rowY + 5, 2, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.text);
      doc.text(trunc(cat.category, 28), tableX + 10, rowY + 7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(String(n(cat.courseCount)), tableX + 80, rowY + 7);
      doc.text(String(n(cat.enrollments)), tableX + 112, rowY + 7);

      const rate = n(cat.completionRate);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(rate >= 70 ? COLORS.success : rate >= 40 ? COLORS.warning : COLORS.danger));
      doc.text(`${rate}%`, tableX + 148, rowY + 7);
      y += 11;
    });
    y += 8;
  }

  // ===== COURSE COMPLETION DATA =====
  if (data.courseCompletionData && data.courseCompletionData.length > 0) {
    checkPageBreak(50);
    sectionTitle('Course Completion Details', 'Completion rates and time spent per course');

    const tableX = margin;
    drawRoundedRect(tableX, y, contentWidth, 9, 2, COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.white);
    doc.text('Course', tableX + 4, y + 6);
    doc.text('Category', tableX + 65, y + 6);
    doc.text('Enrolled', tableX + 100, y + 6);
    doc.text('Done', tableX + 120, y + 6);
    doc.text('Rate', tableX + 137, y + 6);
    doc.text('Avg Time', tableX + 155, y + 6);
    y += 10;

    data.courseCompletionData.slice(0, 15).forEach((course, i) => {
      checkPageBreak(11);
      const rowY = y;
      const bgColor = i % 2 === 0 ? COLORS.bgLight : COLORS.white;
      drawRoundedRect(tableX, rowY, contentWidth, 10, 1, bgColor);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.text);
      doc.text(trunc(course.title, 30), tableX + 4, rowY + 7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(trunc(course.category, 15), tableX + 65, rowY + 7);
      doc.setTextColor(...COLORS.text);
      doc.text(s(course.totalEnrollments, '0'), tableX + 104, rowY + 7);
      doc.text(s(course.completed, '0'), tableX + 123, rowY + 7);

      const rate = n(course.completionRate);
      doc.setTextColor(...(rate >= 70 ? COLORS.success : rate >= 40 ? COLORS.warning : COLORS.danger));
      doc.setFont('helvetica', 'bold');
      doc.text(`${rate}%`, tableX + 137, rowY + 7);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.textLight);
      doc.text(`${n(course.avgTimeSpent)}h`, tableX + 158, rowY + 7);
      y += 11;
    });
    y += 8;
  }

  // ===== COURSE SUMMARY CARDS =====
  if (data.courseCompletionData && data.courseCompletionData.length > 0) {
    const total = data.courseCompletionData.length;
    const totalEnrollments = data.courseCompletionData.reduce((s, c) => s + n(c.totalEnrollments), 0);
    const avgCompletion = total > 0 ? Math.round(data.courseCompletionData.reduce((s, c) => s + n(c.completionRate), 0) / total) : 0;
    const highPerformers = data.courseCompletionData.filter(c => n(c.completionRate) >= 70).length;

    checkPageBreak(30);
    sectionTitle('Course Summary');

    const cardW = (contentWidth - 9) / 4;
    const cardH = 22;

    const courseCards = [
      { label: 'Total Courses', value: String(total) },
      { label: 'Total Enrollments', value: String(totalEnrollments) },
      { label: 'Avg Completion Rate', value: `${avgCompletion}%` },
      { label: 'High Performers (70%+)', value: String(highPerformers) },
    ];

    courseCards.forEach((card, i) => {
      const cardX = margin + i * (cardW + 3);
      drawRoundedRect(cardX, y, cardW, cardH, 3, COLORS.white, COLORS.border);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...COLORS.primary);
      doc.text(card.value, cardX + cardW / 2, y + 9, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(card.label, cardX + cardW / 2, y + 16, { align: 'center' });
    });
    y += cardH + 10;
  }

  // ===== USER SUMMARY CARDS =====
  if (data.userReportData && data.userReportData.length > 0) {
    const totalUsers = data.userReportData.length;
    const activeUsers = data.userReportData.filter(u => s(u.status, '').toLowerCase() === 'active').length;
    const inactiveUsers = totalUsers - activeUsers;
    const withCourses = data.userReportData.filter(u => n(u.coursesEnrolled) > 0).length;

    checkPageBreak(30);
    sectionTitle('User Summary');

    const cardW = (contentWidth - 9) / 4;
    const cardH = 22;

    const userCards = [
      { label: 'Total Users', value: String(totalUsers) },
      { label: 'Active Users', value: String(activeUsers) },
      { label: 'Inactive Users', value: String(inactiveUsers) },
      { label: 'With Enrollments', value: String(withCourses) },
    ];

    userCards.forEach((card, i) => {
      const cardX = margin + i * (cardW + 3);
      drawRoundedRect(cardX, y, cardW, cardH, 3, COLORS.white, COLORS.border);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...COLORS.primary);
      doc.text(card.value, cardX + cardW / 2, y + 9, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(card.label, cardX + cardW / 2, y + 16, { align: 'center' });
    });
    y += cardH + 10;
  }

  // ===== USER REPORT =====
  if (data.userReportData && data.userReportData.length > 0) {
    checkPageBreak(50);
    sectionTitle('User Report', `${data.userReportData.length} users in the system`);

    const tableX = margin;
    drawRoundedRect(tableX, y, contentWidth, 9, 2, COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.white);
    doc.text('Name', tableX + 4, y + 6);
    doc.text('Email', tableX + 45, y + 6);
    doc.text('Role', tableX + 100, y + 6);
    doc.text('Status', tableX + 120, y + 6);
    doc.text('Courses', tableX + 140, y + 6);
    doc.text('Points', tableX + 160, y + 6);
    y += 10;

    data.userReportData.slice(0, 20).forEach((user, i) => {
      checkPageBreak(11);
      const rowY = y;
      const bgColor = i % 2 === 0 ? COLORS.bgLight : COLORS.white;
      drawRoundedRect(tableX, rowY, contentWidth, 10, 1, bgColor);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.text);
      doc.text(trunc(user.fullName, 20), tableX + 4, rowY + 7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(trunc(user.email, 25), tableX + 45, rowY + 7);
      doc.setTextColor(...COLORS.text);
      doc.text(s(user.role, '—'), tableX + 100, rowY + 7);

      const isActive = s(user.status, '').toLowerCase() === 'active';
      doc.setTextColor(...(isActive ? COLORS.success : COLORS.danger));
      doc.text(s(user.status, '—'), tableX + 120, rowY + 7);

      doc.setTextColor(...COLORS.text);
      doc.text(`${n(user.coursesCompleted)}/${n(user.coursesEnrolled)}`, tableX + 143, rowY + 7);
      doc.setTextColor(...COLORS.primary);
      doc.setFont('helvetica', 'bold');
      doc.text(s(user.totalPoints, '0'), tableX + 162, rowY + 7);
      y += 11;
    });
    y += 8;
  }

  // ===== USER GROWTH =====
  if (data.userGrowth && data.userGrowth.length > 0) {
    checkPageBreak(75);
    sectionTitle('User Growth', 'Monthly new user registrations');

    const chartX = margin + 12;
    const chartY = y;
    const chartW = contentWidth - 20;
    const chartH = 45;

    drawRoundedRect(margin, chartY - 4, contentWidth, chartH + 18, 3, COLORS.white, COLORS.border);

    const maxNew = Math.max(...data.userGrowth.map(d => n(d.newUsers)), 1);
    const barCount = data.userGrowth.length;
    const barW = (chartW / barCount) * 0.6;
    const gap = (chartW / barCount) * 0.4;

    // Y-axis grid
    const ySteps = 4;
    doc.setFontSize(6);
    doc.setTextColor(...COLORS.textLight);
    for (let i = 0; i <= ySteps; i++) {
      const val = Math.round((maxNew / ySteps) * (ySteps - i));
      const lineY = chartY + (i / ySteps) * chartH;
      doc.text(String(val), margin + 3, lineY + 1);
      doc.setDrawColor(240, 240, 240);
      doc.setLineWidth(0.2);
      doc.line(chartX, lineY, chartX + chartW, lineY);
    }

    data.userGrowth.forEach((d, i) => {
      const barH = (n(d.newUsers) / maxNew) * chartH;
      const bx = chartX + i * (chartW / barCount) + gap / 2;
      const by = chartY + chartH - barH;

      doc.setFillColor(...COLORS.blue);
      doc.rect(bx, by, barW, Math.max(barH, 0.5), 'F');

      // Value on top
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.text);
      doc.text(String(n(d.newUsers)), bx + barW / 2, by - 2, { align: 'center' });

      // Month label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textLight);
      doc.text(s(d.month, ''), bx + barW / 2, chartY + chartH + 6, { align: 'center' });
    });

    y = chartY + chartH + 18;
  }

  // ===== USER ROLE DISTRIBUTION =====
  if (data.roleDistribution && data.roleDistribution.length > 0) {
    checkPageBreak(50);
    sectionTitle('User Role Distribution', 'Breakdown of users by role');

    const total = data.roleDistribution.reduce((s, r) => s + n(r.count), 0);

    drawRoundedRect(margin, y, contentWidth, 35, 3, COLORS.white, COLORS.border);

    // Draw donut as list of segments (since jsPDF doesn't have arc drawing easily)
    const centerX = margin + 25;
    const centerY = y + 18;
    const radius = 12;
    let startAngle = 0;

    data.roleDistribution.forEach((role) => {
      const pct = total > 0 ? (n(role.count) / total) * 100 : 0;
      const sweepAngle = (pct / 100) * 360;
      const rgb = hexToRgb(role.color);

      // Draw arc segments as small lines
      doc.setDrawColor(...rgb);
      doc.setLineWidth(4);
      const step = 2;
      for (let a = startAngle; a < startAngle + sweepAngle; a += step) {
        const rad1 = ((a - 90) * Math.PI) / 180;
        const rad2 = (((a + step) - 90) * Math.PI) / 180;
        const x1 = centerX + radius * Math.cos(rad1);
        const y1 = centerY + radius * Math.sin(rad1);
        const x2 = centerX + radius * Math.cos(rad2);
        const y2 = centerY + radius * Math.sin(rad2);
        doc.line(x1, y1, x2, y2);
      }
      startAngle += sweepAngle;
    });

    // Center circle (to create donut)
    doc.setFillColor(...COLORS.white);
    doc.circle(centerX, centerY, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.text);
    doc.text(String(total), centerX, centerY + 1, { align: 'center' });

    // Legend
    let legendX = margin + 50;
    let legendY = y + 8;
    data.roleDistribution.forEach((role, i) => {
      const pct = total > 0 ? Math.round((n(role.count) / total) * 100) : 0;
      const rgb = hexToRgb(role.color);
      doc.setFillColor(...rgb);
      doc.circle(legendX, legendY, 2, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.text);
      doc.text(`${s(role.role)} — ${n(role.count)} (${pct}%)`, legendX + 4, legendY + 1);
      legendY += 6;

      // Wrap to next column
      if (i === 3 && data.roleDistribution!.length > 4) {
        legendX += 60;
        legendY = y + 8;
      }
    });

    y += 42;
  }

  // ===== SKILLS ASSESSMENT =====
  if (data.combinedAnalytics?.skillsAssessment && data.combinedAnalytics.skillsAssessment.length > 0) {
    checkPageBreak(50);
    sectionTitle('Skills Assessment', 'Average scores across skill areas');

    data.combinedAnalytics.skillsAssessment.forEach((skill) => {
      checkPageBreak(14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      doc.text(s(skill.skill), margin + 4, y + 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(`${n(skill.users)} users`, margin + 80, y + 4);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text(`${n(skill.score)}%`, pageWidth - margin - 4, y + 4, { align: 'right' });

      drawProgressBar(margin + 4, y + 7, contentWidth - 8, 4, n(skill.score), COLORS.accent);
      y += 14;
    });
    y += 8;
  }

  // ===== CERTIFICATION DISTRIBUTION =====
  if (data.combinedAnalytics?.certificationDistribution && data.combinedAnalytics.certificationDistribution.length > 0) {
    checkPageBreak(40);
    sectionTitle('Certification Distribution');

    data.combinedAnalytics.certificationDistribution.forEach((cert) => {
      checkPageBreak(12);
      const rgb = hexToRgb(cert.color);
      doc.setFillColor(...rgb);
      doc.circle(margin + 6, y + 3, 3, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      doc.text(s(cert.type), margin + 14, y + 5);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text(`${n(cert.percentage)}%`, pageWidth - margin - 4, y + 5, { align: 'right' });
      y += 10;
    });
    y += 8;
  }

  // ===== COMPLETION TRENDS =====
  if (data.combinedAnalytics?.completionTrends && data.combinedAnalytics.completionTrends.length > 0) {
    checkPageBreak(60);
    sectionTitle('Completion Trends', 'Monthly enrollment vs completion');

    const trends = data.combinedAnalytics.completionTrends;
    const maxVal = Math.max(...trends.map(t => Math.max(n(t.enrolled), n(t.completed), n(t.target))), 1);
    const barAreaH = 45;
    const barGroupW = (contentWidth - 10) / trends.length;

    drawRoundedRect(margin, y - 2, contentWidth, barAreaH + 20, 3, COLORS.white, COLORS.border);

    trends.forEach((trend, i) => {
      const groupX = margin + 5 + i * barGroupW;
      const barW = (barGroupW - 8) / 2;

      const enrollH = (n(trend.enrolled) / maxVal) * barAreaH;
      const completeH = (n(trend.completed) / maxVal) * barAreaH;

      doc.setFillColor(...COLORS.primary);
      doc.rect(groupX, y + barAreaH - enrollH, barW, Math.max(enrollH, 0.5), 'F');

      doc.setFillColor(...COLORS.success);
      doc.rect(groupX + barW + 2, y + barAreaH - completeH, barW, Math.max(completeH, 0.5), 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textLight);
      doc.text(s(trend.month, ''), groupX + barGroupW / 2 - 4, y + barAreaH + 8, { align: 'center' });
    });

    y += barAreaH + 22;

    // Legend
    doc.setFillColor(...COLORS.primary);
    doc.circle(margin + 4, y, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.textLight);
    doc.text('Enrolled', margin + 9, y + 1);

    doc.setFillColor(...COLORS.success);
    doc.circle(margin + 34, y, 2, 'F');
    doc.text('Completed', margin + 39, y + 1);
    y += 10;
  }

  // ===== DAILY ACTIVE USERS =====
  if (data.loginActivity && data.loginActivity.length > 0) {
    checkPageBreak(80);
    sectionTitle('Daily Active Users', 'Unique user logins per day (past 30 days)');

    // Summary stats
    const dauValues = data.loginActivity.map(d => n(d.activeUsers));
    const avgDau = dauValues.length > 0 ? Math.round(dauValues.reduce((s, v) => s + v, 0) / dauValues.length) : 0;
    const peakDau = Math.max(...dauValues, 0);
    const todayDau = dauValues.length > 0 ? dauValues[dauValues.length - 1] : 0;

    const summaryW = (contentWidth - 6) / 3;
    drawRoundedRect(margin, y, contentWidth, 18, 3, COLORS.white, COLORS.border);
    const summaryItems = [
      { label: 'Avg Daily Users', value: String(avgDau) },
      { label: 'Peak Users', value: String(peakDau) },
      { label: "Today's Users", value: String(todayDau) },
    ];
    summaryItems.forEach((item, i) => {
      const ix = margin + 4 + i * (summaryW + 2);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...COLORS.primary);
      doc.text(item.value, ix + summaryW / 2, y + 8, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.textLight);
      doc.text(item.label, ix + summaryW / 2, y + 14, { align: 'center' });
    });
    y += 24;

    // Line chart
    const chartX = margin + 12;
    const chartY = y;
    const chartW = contentWidth - 20;
    const chartH = 40;

    drawRoundedRect(margin, chartY - 4, contentWidth, chartH + 14, 3, COLORS.white, COLORS.border);

    const maxUsers = Math.max(...dauValues, 1);
    const ySteps = 4;

    doc.setFontSize(6);
    doc.setTextColor(...COLORS.textLight);
    for (let i = 0; i <= ySteps; i++) {
      const val = Math.round((maxUsers / ySteps) * (ySteps - i));
      const lineY = chartY + (i / ySteps) * chartH;
      doc.text(String(val), margin + 3, lineY + 1);
      doc.setDrawColor(240, 240, 240);
      doc.setLineWidth(0.2);
      doc.line(chartX, lineY, chartX + chartW, lineY);
    }

    const dauPoints: { x: number; y: number }[] = [];
    const dauDivisor = data.loginActivity.length > 1 ? data.loginActivity.length - 1 : 1;
    data.loginActivity.forEach((d, i) => {
      dauPoints.push({
        x: chartX + (i / dauDivisor) * chartW,
        y: chartY + chartH - (n(d.activeUsers) / maxUsers) * chartH,
      });
    });

    doc.setDrawColor(...COLORS.blue);
    doc.setLineWidth(0.6);
    for (let i = 1; i < dauPoints.length; i++) {
      doc.line(dauPoints[i - 1].x, dauPoints[i - 1].y, dauPoints[i].x, dauPoints[i].y);
    }

    dauPoints.forEach((p) => {
      doc.setFillColor(...COLORS.blue);
      doc.circle(p.x, p.y, 0.8, 'F');
    });

    // Show a few date labels
    const labelStep = Math.max(1, Math.floor(data.loginActivity.length / 6));
    data.loginActivity.forEach((d, i) => {
      if (i % labelStep === 0 || i === data.loginActivity!.length - 1) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5);
        doc.setTextColor(...COLORS.textLight);
        const dateLabel = s(d.date, '').slice(-5); // MM-DD
        doc.text(dateLabel, dauPoints[i].x, chartY + chartH + 5, { align: 'center' });
      }
    });

    y = chartY + chartH + 14;
  }

  // ===== ACTIVITY HEATMAP =====
  if (data.activityHeatmap && data.activityHeatmap.length > 0) {
    checkPageBreak(65);
    sectionTitle('Activity Heatmap', 'User activity patterns by day and hour');

    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const hours = [6, 8, 10, 12, 14, 16, 18, 20, 22];
    const maxCount = Math.max(...data.activityHeatmap.map(d => n(d.count)), 1);

    const cellW = (contentWidth - 25) / hours.length;
    const cellH = 6;
    const startX = margin + 20;

    drawRoundedRect(margin, y, contentWidth, days.length * cellH + 18, 3, COLORS.white, COLORS.border);

    // Hour labels
    hours.forEach((hour, i) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textLight);
      const label = hour <= 12 ? `${hour}am` : `${hour - 12}pm`;
      doc.text(label, startX + i * cellW + cellW / 2, y + 6, { align: 'center' });
    });

    const gridY = y + 9;

    days.forEach((day, di) => {
      // Day label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.textLight);
      doc.text(day, margin + 4, gridY + di * cellH + 4);

      hours.forEach((hour, hi) => {
        const entry = data.activityHeatmap!.find(e => e.day === day && e.hour === hour);
        const count = entry ? n(entry.count) : 0;
        const intensity = count / maxCount;

        // Interpolate color: white -> accent based on intensity
        const r = Math.round(255 - intensity * (255 - COLORS.accent[0]));
        const g = Math.round(255 - intensity * (255 - COLORS.accent[1]));
        const b = Math.round(255 - intensity * (255 - COLORS.accent[2]));

        doc.setFillColor(r, g, b);
        doc.rect(startX + hi * cellW + 0.5, gridY + di * cellH, cellW - 1, cellH - 1, 'F');
      });
    });

    y = gridY + days.length * cellH + 8;
  }

  // ===== ENROLLMENT TIMELINE =====
  if (data.enrollmentTimeline && data.enrollmentTimeline.length > 0) {
    checkPageBreak(65);
    sectionTitle('Enrollment Timeline', 'Weekly enrollments vs completions');

    const chartY = y;
    const chartH = 40;
    const chartX = margin + 12;
    const chartW = contentWidth - 20;

    drawRoundedRect(margin, chartY - 4, contentWidth, chartH + 22, 3, COLORS.white, COLORS.border);

    const maxVal = Math.max(...data.enrollmentTimeline.map(d => Math.max(n(d.enrollments), n(d.completions))), 1);
    const barCount = data.enrollmentTimeline.length;
    const groupW = chartW / barCount;
    const barW = (groupW - 4) / 2;

    // Y-axis grid
    const ySteps = 4;
    doc.setFontSize(6);
    doc.setTextColor(...COLORS.textLight);
    for (let i = 0; i <= ySteps; i++) {
      const val = Math.round((maxVal / ySteps) * (ySteps - i));
      const lineY = chartY + (i / ySteps) * chartH;
      doc.text(String(val), margin + 3, lineY + 1);
      doc.setDrawColor(240, 240, 240);
      doc.setLineWidth(0.2);
      doc.line(chartX, lineY, chartX + chartW, lineY);
    }

    data.enrollmentTimeline.forEach((d, i) => {
      const gx = chartX + i * groupW;

      const enrollH = (n(d.enrollments) / maxVal) * chartH;
      const completeH = (n(d.completions) / maxVal) * chartH;

      doc.setFillColor(...COLORS.blue);
      doc.rect(gx + 1, chartY + chartH - enrollH, barW, Math.max(enrollH, 0.5), 'F');

      doc.setFillColor(...COLORS.success);
      doc.rect(gx + barW + 2, chartY + chartH - completeH, barW, Math.max(completeH, 0.5), 'F');

      // Week label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5);
      doc.setTextColor(...COLORS.textLight);
      doc.text(s(d.week, ''), gx + groupW / 2, chartY + chartH + 5, { align: 'center' });
    });

    // Legend
    const legendY = chartY + chartH + 10;
    doc.setFillColor(...COLORS.blue);
    doc.circle(margin + 4, legendY, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.textLight);
    doc.text('Enrollments', margin + 9, legendY + 1);

    doc.setFillColor(...COLORS.success);
    doc.circle(margin + 40, legendY, 2, 'F');
    doc.text('Completions', margin + 45, legendY + 1);

    y = legendY + 10;
  }

  // ===== FOOTER =====
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const fY = pageHeight - 10;
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(margin, fY - 4, pageWidth - margin, fY - 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.textLight);
    doc.text('Magnetix Learning Platform — Confidential', margin, fY);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, fY, { align: 'right' });
  }

  const filename = `Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};
